import {
  Box,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 as Delete, Edit, Plus } from "lucide-react";
import React, { FC } from "react";
import { Link as RouteLink } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";

import { creditEvaluationsKeys } from "api/creditEvaluations/queries";
import { CreditEvaluationsType } from "api/creditEvaluations/types";
import { deleteCustomerSpouse as deleteCustomerSpouseAPI } from "api/customers";
import { ReactComponent as HubspotSVG } from "assets/icons/hubspot.svg";
import { hasPermissions } from "components/stores/UserStore";
import { dateFormatter } from "components/Table/formatters/dateFormatter";
import { priceFormatter } from "components/Table/formatters/priceFormatter";
import UltimateButton from "components/UltimateButton";
import CustomerField from "containers/customers/details/components/CustomerField";
import dayjs from "utils/dayjs";
import { getFileLink } from "utils/getFile";
import { useModal } from "utils/hooks/useModal";

import CreditEvaluationAffordabilityForm from "./CreditEvaluationAffordabilityForm";
import CreditEvaluationCustomer from "./CreditEvaluationCustomer";
import EditHubspotDealIdModal from "./EditHubspotDealId/EditHubspotDealIdModal";
import AddSpouseModal from "../components/AddSpouse/AddSpouseModal";

interface CreditEvaluationOverviewProps {
  creditEvaluation?: CreditEvaluationsType;
  isLoading: boolean;
}

export const averageAgeFormatter = (months: number | undefined) => {
  if (!months) return "-";

  const years = Math.floor(months / 12);
  months = Math.floor(months % 12);

  if (years) {
    return `${years} Year${years !== 1 ? "s" : ""}, ${months} Month${
      months !== 1 ? "s" : ""
    }`;
  } else return `${months} Month${months !== 1 ? "s" : ""}`;
};

export const fromNowMonths = (date: Date | undefined) => {
  if (!date) return "-";

  let months = dayjs().diff(dayjs(date), "months");

  const years = Math.floor(months / 12);
  months = Math.floor(months % 12);

  if (years) {
    return `${years} Year${years !== 1 ? "s" : ""}, ${months} Month${
      months !== 1 ? "s" : ""
    }`;
  } else return `${months} Month${months !== 1 ? "s" : ""}`;
};

const CreditEvaluationOverview: FC<CreditEvaluationOverviewProps> = ({
  creditEvaluation,
  isLoading,
}) => {
  const queryClient = useQueryClient();

  const { mutateAsync: deleteCustomerSpouse } = useMutation(async () => {
    const res = await deleteCustomerSpouseAPI(
      creditEvaluation?.customer?._id ?? "",
    );
    await queryClient.invalidateQueries(
      creditEvaluationsKeys.details(creditEvaluation?._id ?? ""),
    );
    return res.data;
  });

  const {
    isOpen: isOpenAddSpouse,
    handleClose: handleCloseAddSpouse,
    handleOpen: handleOpenAddSpouse,
  } = useModal<CreditEvaluationsType>();

  const {
    isOpen: isOpenEditHubspotDealId,
    handleClose: handleCloseEditHubspotDealId,
    handleOpen: handleOpenEditHubspotDealId,
  } = useModal<CreditEvaluationsType>();

  return (
    <>
      <Paper sx={{ width: "100%" }}>
        <Grid container spacing={2} sx={{ p: { xs: 1.5, sm: 3.5 } }}>
          <Grid item sx={{ textAlign: "left" }} xs={12} lg={4}>
            <CreditEvaluationCustomer customer={creditEvaluation?.customer} />
          </Grid>
          <Grid item sx={{ textAlign: "left" }} xs={12} lg={4}>
            <CreditEvaluationAffordabilityForm
              initialValues={{
                _id: creditEvaluation?._id || "",
                affordability: creditEvaluation?.affordability,
              }}
            />
          </Grid>
          <Grid item sx={{ textAlign: "right" }} xs={2} lg={2}>
            {creditEvaluation?.customer?.hubspotId && (
              <Link
                href={`https://app.hubspot.com/contacts/44366077/contact/${creditEvaluation.customer.hubspotId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <HubspotSVG style={{ height: "50px" }} />
              </Link>
            )}
          </Grid>

          <Grid item sx={{ textAlign: "right" }} xs={10} lg={1.5}>
            <Link
              component={RouteLink}
              to={getFileLink(creditEvaluation?.html.replace("html", "pdf"))}
              sx={{ display: "block" }}
            >
              PDF Report
            </Link>
            <Link
              component={RouteLink}
              to={getFileLink(creditEvaluation?.html)}
            >
              HTML Report
            </Link>
          </Grid>

          <Grid item sx={{ textAlign: "left" }} xs={12} lg={4}>
            <CustomerField
              isLoading={isLoading}
              label="Last Report Date"
              value={dateFormatter(creditEvaluation?.reportDate)}
            />
            <CustomerField
              isLoading={isLoading}
              label="Average age of open revolving credit"
              value={averageAgeFormatter(
                creditEvaluation?.averageMonthsOfOpenRevolvingCredit,
              )}
            />
            <Stack
              sx={{
                py: creditEvaluation?.customer?.spouse ? 1.5 : 1,
                pl: 2,
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
              }}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" color="#000" fontWeight="bold">
                Spouse
              </Typography>

              {isLoading ? (
                <Skeleton height={20} width={120} />
              ) : creditEvaluation?.customer?.spouse ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Link
                    component={RouterLink}
                    to={`/customers/${creditEvaluation?.customer?.spouse._id}`}
                    sx={{ fontSize: 14 }}
                  >
                    {creditEvaluation?.customer?.spouse.firstName}{" "}
                    {creditEvaluation?.customer?.spouse.lastName}
                  </Link>

                  {hasPermissions("update:customers") && (
                    <IconButton
                      color="error"
                      onClick={() => deleteCustomerSpouse()}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              ) : hasPermissions("update:customers") ? (
                <UltimateButton
                  text="Add Spouse"
                  Icon={
                    Plus as React.ComponentType<{
                      size?: number;
                      className?: string;
                      style?: React.CSSProperties;
                    }>
                  }
                  onClick={() => handleOpenAddSpouse()}
                />
              ) : null}
            </Stack>
            <Stack
              sx={{
                py: creditEvaluation?.customer?.spouse ? 1.5 : 1,
                pl: 2,
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
              }}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" color="#000" fontWeight="bold">
                {/* Spouse */}
                Hubspot Deal ID
              </Typography>

              {isLoading ? (
                <Skeleton height={20} width={120} />
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Link
                    href={`https://app.hubspot.com/contacts/44366077/record/0-3/${creditEvaluation?.hubspotDealId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: 14 }}
                  >
                    {creditEvaluation?.hubspotDealId}
                  </Link>

                  <UltimateButton
                    text="Edit"
                    Icon={
                      Edit as React.ComponentType<{
                        size?: number;
                        className?: string;
                        style?: React.CSSProperties;
                      }>
                    }
                    onClick={() => handleOpenEditHubspotDealId()}
                  />
                </Stack>
              )}
            </Stack>
          </Grid>
          <Grid item sx={{ textAlign: "left" }} xs={12} lg={4}>
            <CustomerField
              isLoading={isLoading}
              label="First credit account"
              value={creditEvaluation?.firstCreditAccount}
            />
          </Grid>
          <Grid item sx={{ textAlign: "left" }} xs={12} lg={4}>
            <CustomerField
              isLoading={isLoading}
              label="Age of file"
              value={
                creditEvaluation?.ageOfFile
                  ? dayjs(creditEvaluation.ageOfFile).fromNow()
                  : "-"
              }
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2} sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Grid item xs={12}>
            <CustomerField
              isLoading={isLoading}
              label={"Late Payments"}
              value={
                creditEvaluation?.latePayments?.map((latePayment) => {
                  return (
                    <Box>
                      {latePayment.paymentPattern.length
                        ? latePayment.paymentPattern.join(", ")
                        : fromNowMonths(latePayment.reportDate)}{" "}
                      - {latePayment.creditor} ({latePayment.rating30}x30,
                      {latePayment.rating60}x60,{latePayment.rating90}x90)
                    </Box>
                  );
                }) ?? "-"
              }
            />
          </Grid>
          <Grid item xs={12}>
            <CustomerField
              isLoading={isLoading}
              label={"Charge-Offs"}
              value={
                creditEvaluation?.chargeOffs?.map((chargeOff) => {
                  return (
                    <Box
                      sx={{
                        color: "#FF0000",
                        fontWeight: "bold",
                      }}
                    >
                      {dateFormatter(chargeOff.reportDate)} -{" "}
                      {chargeOff.creditor} ({priceFormatter(chargeOff.pastdue)})
                    </Box>
                  );
                }) ?? "-"
              }
            />
          </Grid>
        </Grid>
      </Paper>
      {creditEvaluation && (
        <AddSpouseModal
          creditEvaluationId={creditEvaluation._id}
          isOpen={isOpenAddSpouse}
          handleClose={handleCloseAddSpouse}
          customerId={creditEvaluation?.customer?._id || ""}
        />
      )}
      {creditEvaluation && (
        <EditHubspotDealIdModal
          creditEvaluationId={creditEvaluation._id}
          hubspotDealId={creditEvaluation.hubspotDealId}
          isOpen={isOpenEditHubspotDealId}
          handleClose={handleCloseEditHubspotDealId}
        />
      )}
    </>
  );
};

export default CreditEvaluationOverview;
